import React from 'react';
import { Controller, type FieldErrors, type FieldPath } from 'react-hook-form';
import { Input } from '@planne-software/uni/Input';

import { useTranslate } from '@src/core/hooks';
import type { DefaultMessagesKeys } from '@src/i18n/useTranslate';

import type { CardFormValues } from '../core/cardPaymentFormSchema';
import type { CardFormControl } from '../types';
import { Grid } from '@planne-software/uni/Grid';

import { cardFieldSize } from '../styles';

type AddressField = {
  name: FieldPath<CardFormValues>;
  key: 'state' | 'city' | 'district' | 'street' | 'number' | 'complement';
  label: DefaultMessagesKeys;
  required: boolean;
  testId: string;
};

const ADDRESS_FIELDS: AddressField[] = [
  { name: 'billingAddress.state', key: 'state', label: 'checkout_addressState', required: true, testId: 'state' },
  { name: 'billingAddress.city', key: 'city', label: 'checkout_addressCity', required: true, testId: 'city' },
  {
    name: 'billingAddress.district',
    key: 'district',
    label: 'checkout_addressDistrict',
    required: true,
    testId: 'district',
  },
  { name: 'billingAddress.street', key: 'street', label: 'checkout_addressStreet', required: true, testId: 'street' },
  { name: 'billingAddress.number', key: 'number', label: 'checkout_addressNumber', required: true, testId: 'number' },
  {
    name: 'billingAddress.complement',
    key: 'complement',
    label: 'checkout_addressComplement',
    required: false,
    testId: 'complement',
  },
];

type BillingAddressFieldsProps = {
  control: CardFormControl;
  errors: FieldErrors<CardFormValues>;
  disabled: boolean;
};

export const BillingAddressFields = (props: BillingAddressFieldsProps) => {
  const { control, errors, disabled } = props;
  const { i18n } = useTranslate();

  return (
    <>
      {ADDRESS_FIELDS.map(({ name, key, label, required, testId }) => (
        <Grid size={cardFieldSize()} key={name} data-testid={`card-form-address-${testId}`}>
          <Controller
            name={name}
            control={control}
            render={({ field: { ref, value, ...field } }) => (
              <Input
                {...field}
                value={(value as string | null) ?? ''}
                id={name}
                inputRef={ref}
                label={i18n(label)}
                required={required}
                disabled={disabled}
                disableClearable
                error={!!errors.billingAddress?.[key]}
                helperText={errors.billingAddress?.[key]?.message}
                size='medium'
              />
            )}
          />
        </Grid>
      ))}
    </>
  );
};

export default BillingAddressFields;
